import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import type { LinhaConferenciaProdutividade } from './conferenciaAtivacao';

/**
 * PACOTE ÚNICO da Visão de Comissões/Produtividade.
 *
 * A tela fazia 6–8 requisições POR CONSULTOR (itens, hierarquia, cargo...) e a
 * navegação ◀ ▶ virava uma escadinha de "carregando". Este hook busca UMA vez,
 * por competência, tudo que os hooks derivados precisam — e a navegação entre
 * consultores passa a ser 100% em memória.
 */
export interface HierarquiaLinha {
  consultor_id: string;
  consultor_nome: string;
  equipe_nome: string | null;
  em_experiencia: boolean;
  da_diretoria: boolean;
  is_lideranca: boolean;
  is_equipe_qualidade: boolean;
  ativo?: boolean;
  fora_da_meta?: boolean;
}

export interface BundleUsuario {
  id: string;
  nome: string;
  role: string | null;
  funcao: string | null;
  equipe_id: string | null;
  equipe_nome: string | null;
  ativo: boolean;
  desativado_em: string | null;
}

export interface ComissaoBundleVisao {
  ativacao?: { habilitada: boolean; prazo_encerrado: boolean;
    linhas: LinhaConferenciaProdutividade[];
    negativos: Array<{ consultorId: string; itemId: string; ordem: number; valor: number }> };
  competencia: string;
  itens: Array<{
    item_id?: string | null;
    pedido_id?: string | null;
    consultor_id: string | null;
    produto: string | null;
    quantidade: number | null;
    subtotal: number | null;
    status_categoria: string | null;
    data_ativacao: string | null;
    data_venda: string | null;
    quadro_atual: string | null;
    coluna_atual: string | null;
    linhas_telefone: unknown;
    fora_sul?: boolean | null;
    nao_paga_comercial?: boolean | null;
    /** false = venda de outro segmento: paga, mas não conta na meta. */
    pontua_segmento?: boolean | null;
  }>;
  hierarquias: Record<string, HierarquiaLinha[]>;
  cargos: Record<string, string | null>;
  usuarios: BundleUsuario[];
  diretoria_ids: string[];
}

export function useComissaoBundleVisao(competencia: Date, acompanharConferencia = false) {
  const mes = format(competencia, 'yyyy-MM');
  return useQuery({
    queryKey: ['comissao-bundle-visao', mes],
    queryFn: async (): Promise<ComissaoBundleVisao> => {
      const { data, error } = await supabase.functions.invoke('comissao-bundle-visao', {
        body: { competencia: mes },
      });
      if (error) throw error;
      return data as ComissaoBundleVisao;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: acompanharConferencia ? 'always' : false,
    placeholderData: prev => prev,
  });
}
